import React, { useState, useEffect, useContext } from "react";
import styled from "styled-components";

import Button from "core/Button";
import Link from "core/Link";
import CrownIcon from "core/ReusableIcons/Crown";
import {
    BuyBoxRadioGroup,
    BuyBoxRadioItem,
    BuyBoxItemLabel,
    BuyBoxItemList
} from "core/AssetDownload/components/Sidebar/BuyBoxRadio";

import AssetDownloadContext from "core/AssetDownload/Context";
import { useAuth } from "frontend/hooks/useAuth";

import { PHOTO_CHECKOUT } from "data/links";

import {
    NAME_BASIC,
    NAME_STANDARD,
    NAME_PREMIUM,
    KEY_PUBLIC,
    KEY_BASIC,
    KEY_STANDARD,
    KEY_PREMIUM,
    getLicenseSchema
} from "photos/helpers";

import {
    setIsReadyForDownload,
    setPurchaseType
} from "core/AssetDownload/Context/actions";

import { convertPxToRem } from "frontend/components/helpers";

const BuyBoxContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 1rem;
`;

const PurchaseButton = styled(Button)`
    svg {
        top: ${convertPxToRem({ px: -1 })};
        width: ${convertPxToRem({ px: 16 })};
        height: ${convertPxToRem({ px: 16 })};
    }
`;

const PremiumPhotoDescription = ({ price, paidMeta }) => {
    return (
        <div>
            <BuyBoxItemLabel>
                <span>Single Purchase</span>
                <span>{price}</span>
            </BuyBoxItemLabel>
            <BuyBoxItemList>
                <li>
                    {paidMeta.pixelDimensions}, {paidMeta.printDimensions} @{" "}
                    {paidMeta.dpi}
                </li>
                <li>Royalty-Free Commercial License</li>
                <li>Crops & edits permitted</li>
                <li>Directly supports artist</li>
            </BuyBoxItemList>
        </div>
    );
};

const StandardPhotoSelectAccordion = ({
    price,
    paidMeta,
    compMeta,
    selected,
    handleSelect
}) => {
    return (
        <BuyBoxRadioGroup onSelect={handleSelect} selected={selected}>
            <BuyBoxRadioItem name="single-purchase">
                <BuyBoxItemLabel>
                    <span>Single purchase</span>
                    <span>{price}</span>
                </BuyBoxItemLabel>
                {selected === "single-purchase" ? (
                    <BuyBoxItemList>
                        <li>
                            {paidMeta.pixelDimensions},{" "}
                            {paidMeta.printDimensions} @ {paidMeta.dpi}
                        </li>
                        <li>Royalty-Free Commercial License</li>
                        <li>Crops & edits permitted</li>
                        <li>Directly supports artist</li>
                    </BuyBoxItemList>
                ) : null}
            </BuyBoxRadioItem>

            <BuyBoxRadioItem name="comp-download">
                <BuyBoxItemLabel>
                    <span>Attribution required</span>
                    <span>$0</span>
                </BuyBoxItemLabel>
                {selected === "comp-download" ? (
                    <BuyBoxItemList>
                        <li>
                            {compMeta.pixelDimensions},{" "}
                            {compMeta.printDimensions} @ {compMeta.dpi}
                        </li>
                        <li>No commercial use</li>
                        <li>No crops or edits</li>
                        <li>
                            Must attribute artist per{" "}
                            <Link
                                href="/legal/terms-of-use/#photo-licenses"
                                darkMode
                                size="sm"
                            >
                                license
                            </Link>
                        </li>
                    </BuyBoxItemList>
                ) : null}
            </BuyBoxRadioItem>
        </BuyBoxRadioGroup>
    );
};

export const PhotoBuyBox = () => {
    const {
        state: {
            asset: photo,
            assetDownloadConfig: { getPurchaseTypeByKey }
        },
        dispatch
    } = useContext(AssetDownloadContext);

    const { confirmAuth } = useAuth();

    const { isPremium, license } = photo;
    const isPublicDomain = license === "PUBLICDOMAIN";

    const [selected, setSelected] = useState("single-purchase");
    const [purchaseKey, setPurchaseKey] = useState(null);

    const licenseProps = { photo, selected, setSelected };

    const paidSchema = getLicenseSchema(
        photo.isPremium ? NAME_PREMIUM : NAME_STANDARD,
        licenseProps
    );
    const paidMeta = paidSchema.meta;

    const compSchema = getLicenseSchema(NAME_BASIC, licenseProps);
    const compMeta = compSchema.meta;

    const onSubmit = () => {
        dispatch(setPurchaseType(getPurchaseTypeByKey(purchaseKey)));

        if ([KEY_BASIC, KEY_PUBLIC].includes(purchaseKey)) {
            // allow download immediately
            dispatch(setIsReadyForDownload(true));
        } else {
            // navigate to checkout page
            // TODO: when we move to Next, use router.push instead
            window.location.assign(
                PHOTO_CHECKOUT.replace(":photoId", photo.id)
            );
        }
    };

    const handleSelect = name => {
        setSelected(name);
    };

    useEffect(() => {
        if (isPublicDomain) {
            setPurchaseKey(KEY_PUBLIC);
        } else if (selected === "single-purchase") {
            setPurchaseKey(isPremium ? KEY_PREMIUM : KEY_STANDARD);
        } else {
            setPurchaseKey(KEY_BASIC);
        }
    }, [license, selected, isPremium]);

    return (
        <BuyBoxContainer>
            {isPublicDomain ? null : isPremium ? (
                <PremiumPhotoDescription
                    price={paidSchema.price}
                    paidMeta={paidMeta}
                />
            ) : (
                <StandardPhotoSelectAccordion
                    price={paidSchema.price}
                    paidMeta={paidMeta}
                    compMeta={compMeta}
                    selected={selected}
                    handleSelect={handleSelect}
                />
            )}

            <PurchaseButton
                onClick={() => confirmAuth({ onSuccess: onSubmit })}
                styleType="cta"
                darkMode
            >
                {isPremium && <CrownIcon />}
                Get This Photo
            </PurchaseButton>
        </BuyBoxContainer>
    );
};
